import * as React from "react";
import { cn } from "@/lib/utils";

interface PageShellProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  eyebrow?: React.ReactNode;
  stats?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  /** Optional test id for the page title; defaults to "text-page-title". */
  titleTestId?: string;
}

export function PageShell({
  title,
  subtitle,
  actions,
  eyebrow,
  stats,
  children,
  className,
  contentClassName,
  titleTestId = "text-page-title",
}: PageShellProps) {
  return (
    // pb-28 reserves space for the floating "Need help?" launcher (48px tall,
    // 20px from the bottom) plus clearance, so the last card on any page can
    // always scroll fully above it (320px/zoomed reflow requirement).
    <div className={cn("max-w-7xl mx-auto px-4 sm:px-6 pt-6 pb-28 space-y-6", className)}>
      <div className="glass-panel rounded-2xl px-5 py-4 sm:px-6 sm:py-5 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          {eyebrow && (
            <div className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-1">
              {eyebrow}
            </div>
          )}
          <h1
            className="text-xl sm:text-2xl font-semibold tracking-tight text-foreground"
            data-testid={titleTestId}
          >
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{subtitle}</p>
          )}
        </div>
        {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
      </div>

      {stats && (
        <div className="grid gap-3 sm:gap-4 grid-cols-1 min-[400px]:grid-cols-2 lg:grid-cols-4">{stats}</div>
      )}

      <div className={cn("space-y-6", contentClassName)}>{children}</div>
    </div>
  );
}

interface PageStatProps {
  label: React.ReactNode;
  value: React.ReactNode;
  hint?: React.ReactNode;
  icon?: React.ReactNode;
}

export function PageStat({ label, value, hint, icon }: PageStatProps) {
  return (
    <div className="stat-chip p-4 flex items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className="text-2xl font-semibold mt-1 text-foreground break-words">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </div>
      {icon && (
        <div className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center brand-pill">
          {icon}
        </div>
      )}
    </div>
  );
}
